import { AlertCircle, File as FileIcon, FileText, Upload, UploadCloud, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { FilePreviewCard } from '@/components/files/FilePreviewCard'
import { FilePreviewDialog, type PreviewFile } from '@/components/files/FilePreviewDialog'
import { FileUploadModal } from '@/components/files/FileUploadModal'
import { useFileMetadata } from '@/hooks/useFileUpload'
import type { FileUploadConfig, UploadedFile } from '@/types/api/file'
import type { FormField } from '@/types/api/form'
import { cn } from '@/utils/cn'
import {
  fileRulesForField,
  formatFileKeys,
  isMultipleFileField,
  fileNameFromKey,
  isImageFile,
  isPdfFile,
  maxFilesForField,
  parseFileKeys,
  saveFile,
} from '@/utils/file-utils'

export type FileUploadFn = (
  file: File,
  options: { onProgress: (percent: number) => void; signal: AbortSignal }
) => Promise<UploadedFile>

interface FileFieldInputProps {
  field: Pick<FormField, 'id' | 'label' | 'options' | 'validations'>
  /** Stored value: nothing, a plain S3 key, or a JSON array of keys (multiple-file fields). */
  value: string
  /** Receives the new stored value after files were added or removed. */
  onChange: (value: string) => void
  /** Uploads one file for this field; absent means the field cannot upload here (read-only placeholder). */
  upload?: FileUploadFn
  /** Global upload policy: per-file maximum and the allowed types. */
  fileConfig?: Pick<FileUploadConfig, 'maxFileSizeBytes' | 'allowedTypes'> | null
  /** False shows the files without the add/remove controls. */
  canEdit?: boolean
  error?: string | null
  id?: string
  /** Smaller controls, for inline editing on the record page. */
  compact?: boolean
  /** Told when the upload dialog opens or closes (a form can keep its submit button off meanwhile). */
  onUploadingChange?: (uploading: boolean) => void
  /** What the placeholder says when `upload` is absent. */
  unavailableText?: string
}

const CHIP_WIDTH = 176 // w-44
const CHIP_GAP = 6 // gap-1.5
const MORE_BUTTON_WIDTH = 84

function FileChip({ file, onOpen, onRemove }: { file: PreviewFile; onOpen: () => void; onRemove?: () => void }) {
  const image = isImageFile(file.contentType, file.name)
  const previewable = (image || isPdfFile(file.contentType, file.name)) && !!file.url
  return (
    <div className="flex h-8 w-44 shrink-0 items-center gap-1.5 rounded-md border border-neutral-300 bg-white pl-1.5 pr-1">
      <button
        type="button"
        onClick={(event) => { event.stopPropagation(); if (previewable) onOpen(); else saveFile(file.attachmentUrl, file.url) }}
        // Nothing to open or save until the file's links have loaded
        disabled={!file.url && !file.attachmentUrl}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left disabled:cursor-wait disabled:opacity-60"
        title={previewable ? `Preview ${file.name}` : `Download ${file.name}`}
      >
        {image && file.url ? (
          <img src={file.url} alt="" className="h-5 w-5 shrink-0 rounded object-cover" />
        ) : isPdfFile(file.contentType, file.name)
          ? <FileText className="h-4 w-4 shrink-0 text-rose-500" />
          : <FileIcon className="h-4 w-4 shrink-0 text-neutral-400" />}
        <span className="truncate text-xs font-medium text-neutral-700">{file.name}</span>
      </button>
      {onRemove && (
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); onRemove() }}
          className="shrink-0 rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-destructive-600"
          aria-label={`Remove ${file.name}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}

/**
 * The files of a FILE_UPLOAD field and the controls to change them: one file (replace/remove) or
 * several (add, remove, up to the field's limit). Owns the upload dialog. The same component serves
 * the new-record dialog, the record page, the form preview and the public form.
 */
export function FileFieldInput({
  field, value, onChange, upload, fileConfig, canEdit = true, error, id, compact = false,
  onUploadingChange, unavailableText = 'File upload opens on the live form',
}: FileFieldInputProps) {
  const [modalOpen, setModalOpen] = useState(false)
  // Metadata of files uploaded in this session, so their cards show name and size without a lookup
  const [uploaded, setUploaded] = useState<Record<string, UploadedFile>>({})
  const [expanded, setExpanded] = useState(false)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const [rowWidth, setRowWidth] = useState(0)

  const options = field.options as Record<string, unknown> | null
  const validations = field.validations as Record<string, unknown> | null
  const multiple = isMultipleFileField(options)
  const maxFiles = maxFilesForField(options, validations)
  const keys = useMemo(() => parseFileKeys(value), [value])
  const unknownKeys = keys.filter((key) => !uploaded[key])
  const { data: fetched = [], isLoading: metadataLoading } = useFileMetadata(unknownKeys)
  const editable = canEdit && !!upload
  const full = keys.length >= maxFiles

  const files: PreviewFile[] = keys.map((key) => {
    const known = uploaded[key]
    const meta = known ? null : fetched.find((entry) => entry.key === key)
    return {
      key,
      name: known?.name ?? meta?.name ?? fileNameFromKey(key),
      url: known?.downloadUrl ?? meta?.downloadUrl ?? null,
      attachmentUrl: known?.attachmentUrl ?? meta?.attachmentUrl ?? null,
      contentType: known?.contentType ?? meta?.contentType ?? null,
    }
  })

  // The chip row shows as many files as fit its width; the rest sit behind "+N more"
  const showRow = keys.length > 0 && !expanded
  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    setRowWidth(row.clientWidth)
    const observer = new ResizeObserver(() => setRowWidth(row.clientWidth))
    observer.observe(row)
    return () => observer.disconnect()
  }, [showRow])
  const allFit = rowWidth === 0 || keys.length * (CHIP_WIDTH + CHIP_GAP) - CHIP_GAP <= rowWidth
  const visibleCount = allFit
    ? keys.length
    : Math.max(1, Math.floor((rowWidth - MORE_BUTTON_WIDTH) / (CHIP_WIDTH + CHIP_GAP)))

  const openModal = (open: boolean) => {
    setModalOpen(open)
    onUploadingChange?.(open)
  }

  const removeKey = (key: string) => onChange(formatFileKeys(keys.filter((k) => k !== key), multiple))

  const buttonLabel = multiple
    ? `Add ${keys.length > 0 ? 'more files' : `${field.label.toLowerCase()}`}`
    : keys.length > 0 ? 'Replace file' : `Upload ${field.label.toLowerCase()}`

  return (
    <div id={id ?? field.id} className="space-y-1.5" aria-invalid={error ? true : undefined}>
      {keys.length > 0 ? (
        expanded ? (
          <div className="space-y-1.5">
            {keys.map((key, i) => {
              const known = uploaded[key]
              return (
                <FilePreviewCard
                  key={key}
                  fileKey={key}
                  name={known?.name}
                  sizeBytes={known?.sizeBytes}
                  contentType={known?.contentType}
                  downloadUrl={known?.downloadUrl}
                  attachmentUrl={known?.attachmentUrl}
                  metadata={known ? null : fetched.find((entry) => entry.key === key) ?? null}
                  isLoading={!known && metadataLoading}
                  onRemove={editable ? () => removeKey(key) : undefined}
                  onPreview={() => setPreviewIndex(i)}
                />
              )
            })}
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); setExpanded(false) }}
              className="text-xs font-medium text-primary-600 hover:underline"
            >
              Show less
            </button>
          </div>
        ) : (
          <div ref={rowRef} className="flex items-center gap-1.5 overflow-hidden">
            {files.slice(0, visibleCount).map((file, i) => (
              <FileChip
                key={file.key}
                file={file}
                onOpen={() => setPreviewIndex(i)}
                onRemove={editable ? () => removeKey(file.key) : undefined}
              />
            ))}
            {visibleCount < keys.length && (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); setExpanded(true) }}
                className="h-8 shrink-0 rounded-md border border-dashed border-neutral-300 px-2.5 text-xs font-medium text-primary-600 hover:bg-primary-50/40"
              >
                +{keys.length - visibleCount} more
              </button>
            )}
          </div>
        )
      ) : !upload ? null : (
        !canEdit && <p className="text-sm font-normal italic text-neutral-300">No file uploaded</p>
      )}

      {editable && !(multiple && full) && (
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); openModal(true) }}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-300 text-neutral-600 transition-colors hover:border-primary-400 hover:bg-primary-50/40',
            compact ? 'px-3 py-2 text-xs font-medium' : 'px-3 py-2.5 text-sm'
          )}
        >
          {compact ? <Upload className="h-3.5 w-3.5" /> : <UploadCloud className="h-4 w-4" />}
          {buttonLabel}
        </button>
      )}

      {!upload && canEdit && (
        <p className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-400">
          <UploadCloud className="h-4 w-4" />
          {unavailableText}
        </p>
      )}

      {multiple && (
        <p className="text-[11px] text-neutral-400">
          {keys.length} of {maxFiles} file{maxFiles === 1 ? '' : 's'}
          {full ? ' · limit reached' : ''}
        </p>
      )}

      {error && (
        <p className="flex items-center gap-1 text-xs text-rose-600">
          <AlertCircle className="h-3 w-3 shrink-0" />{error}
        </p>
      )}

      <FilePreviewDialog
        files={files}
        index={previewIndex ?? 0}
        onIndexChange={setPreviewIndex}
        open={previewIndex !== null}
        onOpenChange={(open) => { if (!open) setPreviewIndex(null) }}
      />

      {modalOpen && upload && (
        <FileUploadModal
          open
          onOpenChange={openModal}
          title={`Upload ${field.label}`}
          multiple={multiple}
          maxFiles={maxFiles}
          attachedCount={multiple ? keys.length : 0}
          upload={upload}
          onUploaded={(files) => {
            if (files.length === 0) return
            setUploaded((prev) => ({ ...prev, ...Object.fromEntries(files.map((f) => [f.key, f])) }))
            const next = multiple ? [...keys, ...files.map((f) => f.key)] : [files[files.length - 1].key]
            onChange(formatFileKeys(next, multiple))
          }}
          rules={fileRulesForField(validations, fileConfig?.maxFileSizeBytes)}
          catalog={fileConfig?.allowedTypes ?? []}
        />
      )}
    </div>
  )
}
